import {OptionUIModel} from './option-ui-model.js';
import {FilterOption} from './filter-option.js';

import {SortAction} from './sort-action.js';
import {Messages, Product} from '../../@types/wishlist.js';
import {FilterAction} from './filter-action.js';
import {createLabel} from './filter-label.js';

export enum SelectionType {
  MULTI_SELECT = 'multi_select',
  SINGLE_SELECT = 'single_select'
}

export class Filter {
  readonly id: string;

  readonly type: SelectionType;

  readonly isSort: boolean;

  readonly optionGroupIds: string[];

  private readonly options: FilterOption[];

  constructor(
    id: string,
    type: SelectionType,
    options: FilterOption[],
    isSort: boolean = false,
    optionGroupIds: string[] = [],
  ) {
    this.id = id;
    this.type = type;
    this.options = options;
    this.isSort = isSort;
    this.optionGroupIds = optionGroupIds;
  }

  public hasCounts() {
    return this.isSort || this.options.some(option => option.getCount() > 0);
  }

  public getActiveOptions(includeEditState: boolean = true): OptionUIModel[] {
    return this.options.filter(option => option.isActive(includeEditState));
  }

  public hasActiveOptionsBeforeEdit() {
    const activeOptions = this.options.filter(option => option.isActive(false) && !option.isDefault);
    const activeOptionsAfterEdit = this.options.filter(option => option.isActive(true) && !option.isDefault);
    return activeOptions.length > 0 && activeOptionsAfterEdit.length === 0;
  }

  public getTrackingOptions(includeDefault: boolean) {
    const activeOptions = this.options
      .filter(option => option.isActive(false) && (!option.isDefault || includeDefault));
    if (activeOptions.length > 0) {
      const optionsAsString = activeOptions.map(option => option.id).join(',');
      return `${this.id}: ${optionsAsString}`;
    }
    return null;
  }

  public getAppliedActiveAndNotDefaultOptions() {
    return this.options.filter(option => option.isActive(false) && !option.isDefault);
  }

  public sort(products: Product[]) {
    if (!this.isSort) {
      return products;
    }
    return products.sort(this.getActiveSort().compareStrategy);
  }

  public applyFilter(product: Product, includeEditState: boolean) {
    if (this.isSort) {
      return true;
    }

    const activeFilterActions: FilterAction[] = this.options
      .filter(option => option.isActive(includeEditState))
      .filter(option => option.action instanceof FilterAction)
      .map(option => option.action) as FilterAction[];
    if (activeFilterActions.length === 0) {
      return true;
    }

    if (this.isSingleSelectionType()) {
      return activeFilterActions.every(option => option.filterStrategy(product));
    }
    return activeFilterActions.some(option => option.filterStrategy(product));
  }

  public getOptions(groupId?: string): OptionUIModel[] {
    return groupId
      ? this.options.filter(o => o.getCount() > 0 && o.groupId === groupId)
      : this.options.filter(o => o.getCount() > 0);
  }

  public toggleFilterOption(filterOptionId: string, allowRemove: boolean) {
    if (!this.isSingleSelectionType()) {
      this.options.filter(option => option.equalsId(filterOptionId))
        .forEach(option => option.toggleActivationState());
      return;
    }

    const optionToToggle = this.options.find(option => option.id === filterOptionId);
    if (optionToToggle === undefined || (!allowRemove && optionToToggle.isActive(true))) {
      return;
    }

    this.options
      .filter(option => option.equalsId(filterOptionId) || (this.isSingleSelectionType() && option.isActive()))
      .forEach(option => option.toggleActivationState());
  }

  public isEdited() {
    return this.options.some(option => option.isEdited());
  }

  public undoEditState() {
    this.options.forEach(option => option.undoEditState());
  }

  public applyEditState() {
    this.options.forEach(option => option.applyEditState());
  }

  public getActiveSort(): SortAction {
    const activeSortOption = this.options
      .find(o => o.isActive(false));
    return activeSortOption!.action as SortAction;
  }

  private isSingleSelectionType() {
    return this.type === SelectionType.SINGLE_SELECT;
  }

  public resetCount() {
    this.options.forEach(option => option.resetCount());
  }

  public reset() {
    this.options.forEach(option => option.reset());
  }

  public countProducts(filteredProducts: Product[], products: Product[], allFilters: Filter[], includeEditState: boolean) {
    if (this.options.some(option => option.isActive())) {
      const filtersWithoutThisFilter = allFilters.filter(filter => filter.id !== this.id);
      const prefilteredProducts = products
        .filter(product => filtersWithoutThisFilter.every(f => f.applyFilter(product, includeEditState)));
      prefilteredProducts.forEach(product => this.options.forEach(option => option.countProduct(product)));
    } else {
      filteredProducts.forEach(product => this.options.forEach(option => option.countProduct(product)));
    }
  }

  public label(messages: Messages): string {
    return createLabel(messages, 'ents_wishlist_filter_group_', this.id);
  }

  public titleLabelForOptionGroup(optionGroupId: string, messages: Messages): string {
    return createLabel(messages, 'ents_wishlist_filter_group_', optionGroupId);
  }
}
